import uniq from 'lodash-es/uniq';
import {
    LinkedObjects,
    StrollersDetailAddToProfileApiFailureAction,
    StrollersDetailAddToProfileApiRequestAction,
    StrollersDetailAddToProfileApiSuccessAction,
    StrollersDetailChangeVariantFeatureValueSuccessAction,
    StrollersDetailChangeVariantFeatureValueTriggerAction,
    StrollersDetailFetchFailureAction,
    StrollersDetailFetchRequestAction,
    StrollersDetailFetchSuccessAction,
    StrollersDetailLoadMoreSimilarsFailureAction,
    StrollersDetailLoadMoreSimilarsRequestAction,
    StrollersDetailLoadMoreSimilarsSuccessAction,
    StrollersDetailRemoveFromProfileApiFailureAction,
    StrollersDetailRemoveFromProfileApiRequestAction,
    StrollersDetailRemoveFromProfileApiSuccessAction,
    STROLLERS_DETAIL_ADD_TO_PROFILE_API_FAILURE,
    STROLLERS_DETAIL_ADD_TO_PROFILE_API_REQUEST,
    STROLLERS_DETAIL_ADD_TO_PROFILE_API_SUCCESS,
    STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_SUCCESS,
    STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_TRIGGER,
    STROLLERS_DETAIL_FETCH_FAILURE,
    STROLLERS_DETAIL_FETCH_REQUEST,
    STROLLERS_DETAIL_FETCH_SUCCESS,
    STROLLERS_DETAIL_LOAD_MORE_SIMILARS_FAILURE,
    STROLLERS_DETAIL_LOAD_MORE_SIMILARS_REQUEST,
    STROLLERS_DETAIL_LOAD_MORE_SIMILARS_SUCCESS,
    STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_FAILURE,
    STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_REQUEST,
    STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_SUCCESS,
} from 'mk2/apps/strollers/containers/StrollersDetail/StrollersDetail.actions';
import { VariantsOptionsEntity } from 'mk2/apps/strollers/schemas';
import { AST, ASTAdditionalDataFromServer } from 'mk2/apps/wiki/schemas';
import { tupdate } from 'mk2/helpers/types';
import { errorMessageFiltered } from 'mk2/reducers/errorMessage';
import { loadingState, loadingStateFiltered, LoadingState } from 'mk2/reducers/loadingState';
import { combineReducers, Reducer } from 'redux';

export interface StrollerDetailState {
    similarIds: number[];
    strollerId: number;
    strollersOfThisBrandIds: number[];
    strollerQuestionIds: number[];
    strollerReviewIds: number[];
    strollerPendingReviewIds: number[];
    strollerAlikeStrollerIds: number[];
    counsellingId: number;
    loadingState: LoadingState;
    errorMessage: string;
    onProfile: boolean;
    addingToProfileLoadingState: LoadingState;
    removingFromProfileLoadingState: LoadingState;
    loadMoreSimilarsLoadingState: LoadingState;
    hasMoreSimilars: boolean;
    countOfSimilars: number;
    oldProductsCount: number;
    eshopProductsIds: number[];
    cheapestOldProductId: number;
    buyingGuideId: number;
    linkedObjects: LinkedObjects;
    articleIds: number[];
    patternsIds: number[];
    variantsIds: number[];
    variantsOptions: VariantsOptionsEntity;
    baseVariantId: number;
    strollerPhotosIds: number[];
    allStrollerPhotosIds: number[];
    variantsPhotos: Record<number, number[]>;
    relatedStrollersPreviewPhotos: Record<number, number>;
    descriptionAST: AST;
    descriptionASTAdditionalData: ASTAdditionalDataFromServer;
    descriptionPhotos: number[];
    eshopProductsWidgetIds: number[];
}

export const initialStrollerDetailState: StrollerDetailState = {
    loadingState: LoadingState.INIT,
    errorMessage: null,
    similarIds: [],
    strollerId: null,
    strollersOfThisBrandIds: [],
    strollerQuestionIds: [],
    strollerReviewIds: [],
    strollerPendingReviewIds: [],
    strollerAlikeStrollerIds: [],
    onProfile: false,
    addingToProfileLoadingState: LoadingState.INIT,
    removingFromProfileLoadingState: LoadingState.INIT,
    loadMoreSimilarsLoadingState: LoadingState.SUCCESS,
    hasMoreSimilars: false,
    countOfSimilars: null,
    cheapestOldProductId: null,
    eshopProductsIds: [],
    oldProductsCount: null,
    buyingGuideId: null,
    linkedObjects: [],
    counsellingId: null,
    articleIds: [],
    patternsIds: null,
    variantsIds: [],
    variantsOptions: {},
    baseVariantId: null,
    strollerPhotosIds: [],
    allStrollerPhotosIds: [],
    variantsPhotos: {},
    relatedStrollersPreviewPhotos: {},
    descriptionAST: null,
    descriptionASTAdditionalData: {},
    descriptionPhotos: [],
    eshopProductsWidgetIds: [],
};

const linkedObjects: Reducer<LinkedObjects> = (state = [], action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.linkedObjects;
        default:
            return state;
    }
};

const articleIds: Reducer<number[]> = (state = [], action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.articles;
        default:
            return state;
    }
};

const eshopProductsIds: Reducer<number[]> = (state = [], action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.eshopProducts;
        default:
            return state;
    }
};

const similarIds: Reducer<number[]> = (state = [], action: StrollersDetailFetchSuccessAction | StrollersDetailLoadMoreSimilarsSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
        case STROLLERS_DETAIL_LOAD_MORE_SIMILARS_SUCCESS:
            return uniq([...state, ...action.response.result.similars]);
        default:
            return state;
    }
};

const strollerQuestionIds: Reducer<number[]> = (state = [], action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.strollerQuestions;
        default:
            return state;
    }
};

const patternsIds: Reducer<number[]> = (state = [], action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.patterns;
        default:
            return state;
    }
};

const strollerReviewIds: Reducer<number[]> = (state = [], action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.strollerReviews;
        default:
            return state;
    }
};

const strollerPendingReviewIds: Reducer<number[]> = (state = [], action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.strollerPendingReviews;
        default:
            return state;
    }
};

const strollerAlikeStrollerIds: Reducer<number[]> = (state = [], action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.strollerAlikeStrollers;
        default:
            return state;
    }
};

const hasMoreSimilars: Reducer<boolean> = (state = null, action: StrollersDetailFetchSuccessAction | StrollersDetailLoadMoreSimilarsSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
        case STROLLERS_DETAIL_LOAD_MORE_SIMILARS_SUCCESS:
            return action.response.result.hasMoreSimilars;
        default:
            return state;
    }
};

const countOfSimilars: Reducer<number> = (state = null, action: StrollersDetailFetchSuccessAction | StrollersDetailLoadMoreSimilarsSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
        case STROLLERS_DETAIL_LOAD_MORE_SIMILARS_SUCCESS:
            return action.response.result.countOfSimilars;
        default:
            return state;
    }
};

const loadMoreSimilarsLoadingState = (state = null, action:
    StrollersDetailFetchSuccessAction |
    StrollersDetailLoadMoreSimilarsRequestAction |
    StrollersDetailLoadMoreSimilarsSuccessAction |
    StrollersDetailLoadMoreSimilarsFailureAction,
) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
        case STROLLERS_DETAIL_LOAD_MORE_SIMILARS_REQUEST:
        case STROLLERS_DETAIL_LOAD_MORE_SIMILARS_SUCCESS:
        case STROLLERS_DETAIL_LOAD_MORE_SIMILARS_FAILURE:
            return loadingState(undefined, action);
        default:
            return state;
    }
};

const strollerId: Reducer<number> = (state = null, action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.stroller;
        default:
            return state;
    }
};

const strollersOfThisBrandIds: Reducer<number[]> = (state = null, action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.strollersOfThisBrand;
        default:
            return state;
    }
};

const onProfile: Reducer<boolean> = (state = false, action: StrollersDetailFetchSuccessAction | StrollersDetailAddToProfileApiSuccessAction | StrollersDetailRemoveFromProfileApiSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.onProfile;
        case STROLLERS_DETAIL_ADD_TO_PROFILE_API_SUCCESS:
            return true;
        case STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_SUCCESS:
            return false;
        default:
            return state;
    }
};

const oldProductsCount: Reducer<number> = (state = null, action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.oldProductsCount;
        default:
            return state;
    }
};

const cheapestOldProductId: Reducer<number> = (state = null, action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.cheapestOldProduct;
        default:
            return state;
    }
};

const buyingGuideId: Reducer<number> = (state = null, action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.buyingGuide;
        default:
            return state;
    }
};

const counsellingId: Reducer<number> = (state = null, action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.counselling;
        default:
            return state;
    }
};

const variantsIds: Reducer<number[]> = (state = null, action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.variantsIds;
        default:
            return state;
    }
};

const variantsOptions: Reducer<VariantsOptionsEntity> = (state = {}, action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.variantsOptions;
        default:
            return state;
    }
};

const baseVariantId: Reducer<number> = (state = null, action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.baseVariantId;
        default:
            return state;
    }
};

const strollerPhotosIds: Reducer<number[]> = (state = null, action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.strollerPhotos;
        default:
            return state;
    }
};

const allStrollerPhotosIds: Reducer<number[]> = (state = [], action: StrollersDetailFetchSuccessAction | StrollersDetailLoadMoreSimilarsSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.allStrollerPhotos;
        case STROLLERS_DETAIL_LOAD_MORE_SIMILARS_SUCCESS:
            return uniq([...state, ...'response' in action ? action.response.result.strollersPhotos : []]);
        default:
            return state;
    }
};

const variantsPhotos: Reducer = (state = {}, action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.variantsPhotos;
        default:
            return state;
    }
};

const relatedStrollersPreviewPhotos: Reducer = (state = {}, action: StrollersDetailFetchSuccessAction | StrollersDetailLoadMoreSimilarsSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.relatedStrollersPreviewPhotos;
        case STROLLERS_DETAIL_LOAD_MORE_SIMILARS_SUCCESS:
            return tupdate(state, 'response' in action ? action.response.result.strollersPreviewPhotoMap : {});
        default:
            return state;
    }
};

const descriptionAST: Reducer<AST>  = (state = null, action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.descriptionAST;
        default:
            return state;
    }
};

const descriptionASTAdditionalData: Reducer = (state = {}, action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.descriptionASTAdditionalData;
        default:
            return state;
    }
};

const descriptionPhotos: Reducer<number[]>  = (state = [], action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.descriptionPhotos;
        default:
            return state;
    }
};

const eshopProductsWidgetIds: Reducer = (state = [], action: StrollersDetailFetchSuccessAction) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_SUCCESS:
            return action.response.result.eshopProductsWidget;
        default:
            return state;
    }
};

const detailReducer: Reducer<StrollerDetailState> = combineReducers<StrollerDetailState>({
    similarIds,
    strollerId,
    strollersOfThisBrandIds,
    strollerQuestionIds,
    strollerReviewIds,
    strollerPendingReviewIds,
    strollerAlikeStrollerIds,
    onProfile,
    eshopProductsIds,
    cheapestOldProductId,
    buyingGuideId,
    oldProductsCount,
    linkedObjects,
    counsellingId,
    articleIds,
    loadingState: loadingStateFiltered('STROLLERS_DETAIL_FETCH'),
    errorMessage: errorMessageFiltered('STROLLERS_DETAIL_FETCH'),
    addingToProfileLoadingState: loadingStateFiltered('STROLLERS_DETAIL_ADD_TO_PROFILE'),
    removingFromProfileLoadingState: loadingStateFiltered('STROLLERS_DETAIL_REMOVE_FROM_PROFILE'),
    loadMoreSimilarsLoadingState,
    hasMoreSimilars,
    countOfSimilars,
    patternsIds,
    variantsIds,
    variantsOptions,
    baseVariantId,
    strollerPhotosIds,
    allStrollerPhotosIds,
    variantsPhotos,
    relatedStrollersPreviewPhotos,
    descriptionAST,
    descriptionASTAdditionalData,
    descriptionPhotos,
    eshopProductsWidgetIds,
});

export interface StrollersDetailState {
    [slugAndVariantId: string]: StrollerDetailState;
}

declare type StrollersDetailReducerAction =
    StrollersDetailFetchRequestAction
    | StrollersDetailFetchSuccessAction
    | StrollersDetailFetchFailureAction
    | StrollersDetailLoadMoreSimilarsRequestAction
    | StrollersDetailLoadMoreSimilarsSuccessAction
    | StrollersDetailLoadMoreSimilarsFailureAction
    | StrollersDetailAddToProfileApiRequestAction
    | StrollersDetailAddToProfileApiSuccessAction
    | StrollersDetailAddToProfileApiFailureAction
    | StrollersDetailRemoveFromProfileApiRequestAction
    | StrollersDetailRemoveFromProfileApiSuccessAction
    | StrollersDetailRemoveFromProfileApiFailureAction
    | StrollersDetailChangeVariantFeatureValueTriggerAction
    | StrollersDetailChangeVariantFeatureValueSuccessAction;

export const strollersDetailReducer: Reducer<StrollersDetailState> = (
    state: StrollersDetailState = {},
    action: StrollersDetailReducerAction,
) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_REQUEST:
        case STROLLERS_DETAIL_FETCH_SUCCESS:
        case STROLLERS_DETAIL_FETCH_FAILURE:
        case STROLLERS_DETAIL_LOAD_MORE_SIMILARS_REQUEST:
        case STROLLERS_DETAIL_LOAD_MORE_SIMILARS_SUCCESS:
        case STROLLERS_DETAIL_LOAD_MORE_SIMILARS_FAILURE:
        case STROLLERS_DETAIL_ADD_TO_PROFILE_API_REQUEST:
        case STROLLERS_DETAIL_ADD_TO_PROFILE_API_SUCCESS:
        case STROLLERS_DETAIL_ADD_TO_PROFILE_API_FAILURE:
        case STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_REQUEST:
        case STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_SUCCESS:
        case STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_FAILURE:
        case STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_SUCCESS:
        case STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_TRIGGER:
            const slug = action.slug;
            const variantId = 'variantId' in action && action.variantId;
            const slugAndVariantId = !!variantId ? `${slug}-${variantId}` : slug;
            return {
                ...state,
                [slugAndVariantId]: detailReducer(state[slugAndVariantId], action),
            };
        default:
            return state;
    }
};

export default strollersDetailReducer;
